import React, { useState, useMemo, memo } from "react";
import styles from "./analisiDrawdownPortfolioCompare.module.scss";
import { useTranslation } from "react-i18next";
import colorsStore from "shared/store/colorsStore";
import { formatItalianNumber } from "utils/formatUtils";
import { useComparisonLabels } from "../../ComparisonLabelsContext";

type AnalisiDrawdownPortfolioCompareProps = {
  data: any;
  data2: any;
  title: string;
};

const AnalisiDrawdownPortfolioCompareComponent: React.FC<
  AnalisiDrawdownPortfolioCompareProps
> = ({ data, data2, title = "" }) => {
  const dataMaximumDrawdown =
    data.analisi_backtest.analisi_drawdown_max_drawdown_stats;
  const dataMaximumDrawdown2 =
    data2.analisi_backtest.analisi_drawdown_max_drawdown_stats;

  const dataRecovery =
    data.analisi_backtest.analisi_drawdown_recovery_time_stats;

  const dataRecovery2 =
    data2.analisi_backtest.analisi_drawdown_recovery_time_stats;

  const dataMaxParsed = parseMaxGuadagno(dataMaximumDrawdown);
  const dataMaxParsed2 = parseMaxGuadagno(dataMaximumDrawdown2);

  const dataRecoveryParsed = parseRecovery(dataRecovery);
  const dataRecoveryParsed2 = parseRecovery(dataRecovery2);

  useTranslation();
  const labels = useComparisonLabels();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const [columns] = useState(3);
  const totalRows = dataMaximumDrawdown.length + dataRecovery.length + 1;
  const rows = [];

  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row = rowIndex + 1;

    rows.push(row);
  }
  const cells = useMemo(() => {
    const generatedCells = [];
    for (const row of rows) {
      for (let colIndex = 0; colIndex < columns; colIndex++) {
        const cell = {
          row,
          col: colIndex + 1,
        };

        generatedCells.push(cell);
      }
    }

    return generatedCells;
  }, [rows, columns]);
  const renderedCells = useMemo(() => {
    return cells.map((cell) => {
      const { row, col } = cell;

      if (row === 1) {
        return (
          <div
            key={`${row}-${col}`}
            className={[styles.cell, styles.productName].join(" ")}
          >
            {col === 2 && labels.portfolio1Perc}
            {col === 3 && labels.portfolio2Perc}
          </div>
        );
      }
      if (row > 1) {
        if (col === 1 && row > 1 && row < 6) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                dataMaxParsed[row - 2].type === "topLabel"
                  ? styles.topLabelValue
                  : styles.noTopLabelValue,
                row === totalRows
                  ? styles.cellWithNoBorder
                  : row === 5
                    ? styles.cellWithBorderBlack
                    : styles.cellWithBorder,
              ].join(" ")}
            >
              {row > 1 && dataMaxParsed[row - 2].label}
            </div>
          );
        }
        if (col === 1 && row > 5 && row < 9) {
          return (
            <div
              key={`${row}-${col}`}
              className={[
                dataMaxParsed[row - 6].type === "topLabel"
                  ? styles.topLabelValue
                  : styles.noTopLabelValue,
                row === totalRows
                  ? styles.cellWithNoBorder
                  : styles.cellWithBorder,
              ].join(" ")}
            >
              {row > 1 && dataRecoveryParsed[row - 6].label}
            </div>
          );
        }

        //** data maximum drawdown */
        if (col > 1 && row > 1 && row < 6) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div
                      className={[
                        dataMaxParsed[row - 2].type === "topLabel"
                          ? styles.topLabelValue
                          : styles.noTopLabelValue,

                        row === totalRows
                          ? styles.cellWithNoBorder
                          : row === 5
                            ? styles.cellWithBorderBlack
                            : styles.cellWithBorder,
                      ].join(" ")}
                      style={{
                        textAlign: "center",
                        lineHeight: "40px",
                        color: colorsConfig.FinecoBlack,
                      }}
                    >
                      {col === 2 && dataMaxParsed[row - 2].weight}
                      {col === 3 && dataMaxParsed2[row - 2].weight}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        if (col > 1 && row > 5 && row < 9) {
          return (
            <div key={`${row}-${col}`}>
              <div className={styles.cellValueInnerHeader}>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                    }}
                  >
                    <div
                      className={[
                        dataMaxParsed[row - 6].type === "topLabel"
                          ? styles.topLabelValue
                          : styles.noTopLabelValue,

                        row === totalRows
                          ? styles.cellWithNoBorder
                          : styles.cellWithBorder,
                      ].join(" ")}
                      style={{
                        textAlign: "center",
                        lineHeight: "40px",
                        color: colorsConfig.FinecoBlack,
                      }}
                    >
                      {col === 2 && dataRecoveryParsed[row - 6].weight}
                      {col === 3 && dataRecoveryParsed2[row - 6].weight}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }

      return (
        <div key={`${row}-${col}`} className={styles.cellValue}>
          -
        </div>
      );
    });
  }, [cells, data]);

  return (
    <div>
      {title && <div className={styles.title}>{title}</div>}
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columns}`]}`}>
          {renderedCells}
        </div>
      </div>
    </div>
  );
};

export const AnalisiDrawdownPortfolioCompare = memo(
  AnalisiDrawdownPortfolioCompareComponent,
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data) &&
      JSON.stringify(prevProps.data2) === JSON.stringify(nextProps.data2) &&
      prevProps.title === nextProps.title
    );
  },
);

export default AnalisiDrawdownPortfolioCompare;

function formatIsoDateOrDash(value: unknown): string {
  if (typeof value !== "string" || !value.includes("-")) {
    return "-";
  }
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function parseMaxGuadagno(data) {
  const dataMaxParsed = [];
  for (let i = 0; i < data.length; i++) {
    switch (i) {
      case 0:
        dataMaxParsed.push({
          label: data[i].label,
          weight: formatItalianNumber(data[i].weight),
          type: "topLabel",
        });
        break;
      case 1:
        dataMaxParsed.push({
          label: data[i].label,
          weight: formatIsoDateOrDash(data[i].weight),
        });
        break;
      case 2:
        dataMaxParsed.push({
          label: data[i].label,
          weight: formatIsoDateOrDash(data[i].weight),
        });
        break;
      case 3:
        dataMaxParsed.push({
          label: data[i].label,
          weight: formatItalianNumber(data[i].weight),
        });
        break;
    }
  }
  return dataMaxParsed;
}

function parseRecovery(data) {
  const dataRecoveryParsed = [];
  for (let i = 0; i < data.length; i++) {
    switch (i) {
      case 0:
        dataRecoveryParsed.push({
          label: data[i].label,
          weight: formatItalianNumber(data[i].weight),
          type: "topLabel",
        });
        break;
      case 1:
        dataRecoveryParsed.push({
          label: data[i].label,
          weight: formatIsoDateOrDash(data[i].weight),
        });
        break;
      case 2:
        dataRecoveryParsed.push({
          label: data[i].label,
          weight: formatIsoDateOrDash(data[i].weight),
        });
        break;
      // case 3:
      //   dataMaxParsed.push({
      //     label: data[i].label,
      //     weight: data[i].weight.toFixed(2),
      //   });
      //   break;
    }
  }
  return dataRecoveryParsed;
}
